/* eslint-disable import/prefer-default-export */
import React, { Fragment } from 'react';
import uuidv1 from 'uuid/v1';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { startCase } from 'lodash';
import { Link } from 'react-router-dom';
import { FormattedAmount, RoundedTitle, StatusDot, StatusLabel, Tooltip } from '../../components/common';
import { formatDateAs, DateFormats } from '../Dates';
import { getCountryImage, getPaymentMethodImage, getPayoutMethodImage, handleErrorImgCountry, handleErrorImgPaymentMethod, getBankImage } from '../Paths';
import { RefundReason } from '../Constants';

export const amountFormatter = (amount, currency, minDigits) =>
  (typeof amount === 'undefined' || amount === null || amount === '' ? null : (
    <Fragment>
      {currency && <span>{currency} &nbsp;</span>}
      <FormattedAmount minDigits={minDigits}>{amount}</FormattedAmount>
    </Fragment>
  ));

  export const dotFormatter = (text, length) => {
    if (!text) return '';
    return text.length >= length ? `${text.substr(0, length)}...` : text;
  };

export const statusFormatter = (status) => <StatusLabel status={status} />;

export const statusDotFormatter = (status) => <StatusDot status={status} />;

export const statusFilterFormatter = (status, onClose) => <StatusLabel key={status} status={status} className="options" withClose onClose={() => onClose(status)} />;

export const countryFormatterTT = (countryIso, item) => {
  const ttContent = item.countryName || countryIso;

  const tooltipId = uuidv1();
  return (
    <>
      <img src={getCountryImage(countryIso)} className="flag" alt={item.countryName} onError={handleErrorImgCountry} data-tip data-for={tooltipId} />
      {ttContent !== '' && <Tooltip id={tooltipId}>{ttContent}</Tooltip>}
    </>
  );
};

export const countryFormatter = (countryIso, countryName = '') => {
  return <img src={getCountryImage(countryIso)} className="flag" alt={countryName} title={countryName} onError={handleErrorImgCountry} />;
};

export const pmImageFormatter = (code, item = '') => {
  const title = item.paymentCode || item;
  return <img src={getPaymentMethodImage(code)} className="payment-method-logo" alt={title} title={title} onError={handleErrorImgPaymentMethod} />;
};

export const poImageFormatter = (code, item = '') => {
  const title = item.paymentCode || item;
  return <img src={getPayoutMethodImage(code)} className="payment-method-logo" alt={title} title={title} onError={handleErrorImgPaymentMethod} />;
};

export const bankImageFormatter = (bankCode, countryIso, bankName = '') => (
  <img src={getBankImage(bankCode, countryIso)} className="payment-method-logo" alt={bankName || bankCode} title={bankName || bankCode} onError={handleErrorImgPaymentMethod} />
);

export const booleanFormatter = (value) => {
  return value ? (
    <FontAwesomeIcon icon={{ prefix: 'far', iconName: 'check-circle' }} size="lg" className="text-success" />
  ) : (
    <FontAwesomeIcon icon={{ prefix: 'far', iconName: 'times-circle' }} size="lg" className="text-danger" />
  );
};

export const visibleFormatter = (value) => {
  return value ? (
    <FontAwesomeIcon icon={{ prefix: 'fa', iconName: 'eye' }} size="lg" className="text-success" />
  ) : (
    <FontAwesomeIcon icon={{ prefix: 'fa', iconName: 'eye-slash' }} size="lg" className="text-danger" />
  );
};

export const paymentMethodFormatter = (code, name) => (
  <div>
    {pmImageFormatter(code, name)} <span>{name}</span>
  </div>
);

export const iconLabelFormatter = (icon, text, classes = '') =>
  emptyFormatter(
    <div className="icon-label">
      <div className="icon">
        <FontAwesomeIcon icon={icon} className={classes} data-tip />
      </div>
      <div className="label">{text}</div>
    </div>
  );

export const emptyFormatter = (value) => ((typeof value === 'string' && value.trim() === '') || value === null || value === undefined ? '—' : value);

export const anonymousFormatter = (value) => ((typeof value === 'string' && value.trim() === '') || value === null || value === undefined ? 'Anonymous' : value);

export const fileNameFormatter = (fileName) => {
  const splitName = fileName.split('.');
  const name = splitName[0];
  const ext = splitName.length > 1 ? `.${splitName[1]}` : '';
  const formattedName = name.length > 24 ? `${name.substring(0, 19)}...${name.substring(name.length - 2, name.length)}${ext}` : `${name}${ext}`;
  return formattedName;
};

export const tranferTypeFormatter = (type) => <RoundedTitle className={`title-${type.toLowerCase()}`} title={startCase(type)} />;

export const amountIconFormatter = (amount, currency, type) => (
  <>
    <FontAwesomeIcon className={`arrow-${type.toLowerCase()}`} icon={type === 'DEBIT' ? 'long-arrow-alt-down' : 'long-arrow-alt-up'} />

    <span>&nbsp; {amountFormatter(Math.abs(amount), currency)}</span>
  </>
);

export const refundReasonFormatter = (reason) => {
  const reasonText = RefundReason.find((r) => r.value === reason);
  return reasonText ? reasonText.label : null;
};

export const truncateText = (text, limit = 35) => {
  if (!text) return emptyFormatter(text);

  return (
    <span title={text}>
      {text.length >= limit ? `${text.substr(0, limit - 3)}...` : text}
    </span>
  );
};


export const paymentOrderBatchFormatters = {
  id: id => id,
  merchantId: id => id,
  submissionDate: (date) => formatDateAs(date, DateFormats.RegularDateTime),
  totalTransactions: total => total,
  status: (status) => {
  return statusFormatter(status);
  },
};

export const depositIdFormatter = (depositId) => {
  if (!depositId) {
    return emptyFormatter(depositId);
  }
  return (
    <Link to={`/cashin/${depositId}`} className="deposit-link">
      {depositId}
    </Link>
  );
};

export const paymentOrderFormatters = {
  id: (id) => id,
  paymentReference: (paymentReference) => paymentReference,
  creationDate: (date) => formatDateAs(date, DateFormats.RegularDateTime),
  concept: (text) => truncateText(text),
  description: (description) => truncateText(description),
  currency: (text) => truncateText(text),
  amount: (value, { currency }) => amountFormatter(value, currency),
  latePaymentAmount: (value, { currency }) => amountFormatter(value, currency),
  clientName: (text) => truncateText(text),
  clientLastName: (text) => truncateText(text),
  documentType: (documentType) => documentType,
  clientDocument: (clientDocument) => clientDocument,
  email: (email) => email,
  phone: (phoneNumber) => phoneNumber,
  status: (status) => {
    return statusFormatter(status);
  },
  depositId: (depositId) => depositIdFormatter(depositId),
  batchId: (batchId) => batchId,
  country: (countryCode = '') => countryFormatter(countryCode),
  initialAmount: (value, { currency }) => amountFormatter(value, currency) 
};

const transactionFormatters = {
  creationDate: (creationDate) => {
    return formatDateAs(creationDate, DateFormats.RegularDateTime);
  },
  lastChangeDate: (lastChangeDate) => formatDateAs(lastChangeDate, DateFormats.RegularDateTime),
  merchantAmount: (merchantAmount, { merchantCurrency }) => emptyFormatter(amountFormatter(merchantAmount, merchantCurrency)),
  depositAmount: (value, { depositCurrency }) => amountFormatter(value, depositCurrency),
  creditedAmount: (value, { creditedCurrency }) => amountFormatter(value, creditedCurrency),
  amount: (value, { currency }) => amountFormatter(value, currency),
  fee: (value, { feeCurrency }) => amountFormatter(value, feeCurrency),
  country: (countryCode = '') => countryFormatter(countryCode),
  countryIso: (countryIso = '', row) => countryFormatter(countryIso, row.countryName, row),
  status: (status) => statusFormatter(status),
  type: (type) => statusFormatter(type),
  clientDocument: (clientDocument) => emptyFormatter(clientDocument),
  paymentMethodName: (paymentMethodName) => emptyFormatter(paymentMethodName),
  reason: (reason) => <span title={reason}>{reason.length >= 35 ? `${reason.substr(0, 32)}...` : reason}</span>,
  merchantName: (merchantName, { idMerchant }) => {
    return `${merchantName} (${idMerchant})`;
  },
  refundAttempted: (refundAttempted) => booleanFormatter(refundAttempted),
  crypto: (crypto) => booleanFormatter(crypto),
  externalId: (externalId) => externalId,
  payoutMethod: (value, { payoutMethod, payoutMethodCode }) => {
    if (!payoutMethodCode) {
      return emptyFormatter(payoutMethodCode);
    }
    return poImageFormatter(payoutMethodCode, payoutMethod);
  },
  utr: (utr) => emptyFormatter(utr),
  beneficiaryVerificationStatus: (status) => (status != null ? statusFormatter(status) : emptyFormatter(status)),
  refundReason: (reason) => (reason != null ? refundReasonFormatter(reason) : emptyFormatter(reason)),
  subscriptionId: (subscriptionId) => emptyFormatter(subscriptionId),
  installments: (installments) => booleanFormatter(installments),
};

export { transactionFormatters };
